import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { catchError, combineLatest, filter, map, of, switchMap, take, withLatestFrom } from "rxjs";
import { PageIdentityEnum } from "../../../enums/page-identity.enum";
import { FilterSetApiService } from "../../../services/api-services/filter-set/filter-set.api-service";
import { ITableFilterSetState } from "../interfaces/table-filter-set.sate";
import { ITableFilterState } from "../interfaces/table-filter.state";
import { TableFilterActions } from "../table-filter-state/table-filter.actions";
import { TableFilterSelectors } from "../table-filter-state/table-filter.selectors";
import { DEFAULT_FILTER_SETS } from "./filter-set-static/filter-set-static.data";
import { dropExclusiveConflicts, mergeSetFilters, sameSetFilter } from "./filter-set.utils";
import { FilterSetActions } from "./table-filter-set.actions";
import { TableFilterSetSelectors } from "./table-filter-set.selectors";

@Injectable()
export class TableFilterSetEffects {

    actions$: Actions = inject(Actions);
    store: Store = inject(Store);
    filterApi: FilterSetApiService = inject(FilterSetApiService);

    fetchFilterSets$ = createEffect(() =>
        this.actions$.pipe(ofType(FilterSetActions.fetchFilterSets),
            switchMap((action) => {
                return this.filterApi.getFilterSets()
                    .pipe(
                        map((filters: ITableFilterSetState[]) => {
                            const defaultSets = DEFAULT_FILTER_SETS[action.tableId] ?? [];
                            let mergedFilters = [...defaultSets, ...filters];
                            return FilterSetActions.fetchFilterSetsSuccess({ tableId: action.tableId, filterSets: mergedFilters })
                        }),
                        catchError(() =>
                            of(FilterSetActions.fetchFilterSetsFailed({ tableId: action.tableId, filterSets: DEFAULT_FILTER_SETS[action.tableId] ?? [] }))
                        )
                    )
            })
        )
    );

    groupToggleFilters$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                FilterSetActions.exclusiveGroupToggle,
                FilterSetActions.independentToggle,
                FilterSetActions.setActiveSets
            ),
            switchMap(action =>
                combineLatest([
                    this.store.select(TableFilterSetSelectors.getFilterSets(action.tableId)),
                    this.store.select(TableFilterSelectors.Filters(action.tableId))
                ]).pipe(
                    take(1),
                    map(([sets, current]) => FilterSetActions.applyGroupToggleFilters({
                        tableId: action.tableId,
                        filters: mergeSetFilters(sets ?? [], current ?? [])
                    }))
                )
            )
        )
    );

    // Keep the active filter-set in sync with the applied filters: whenever the sets load OR
    // the filters are (re)set — e.g. restored from the URL on navigating back to the page —
    // activate the set whose definition exactly matches the current applied filters. Reads
    // both filters and sets from the store (not the action) so it works regardless of which
    // of the two arrives first. Single-set matches only; group/multi-set state is driven by
    // the pset param. Guarded against re-activating an already-active set to avoid churn.
    reconcileActiveSet$ = createEffect(() =>
        this.actions$.pipe(
            ofType(FilterSetActions.fetchFilterSetsSuccess, TableFilterActions.setFilters),
            withLatestFrom(this.store.select(s => s)),
            map(([action, storeState]) => {
                const tableState = (storeState as any)['tableState']?.[action.tableId];
                const appliedFilters: ITableFilterState[] = (tableState?.filters ?? []).filter((f: ITableFilterState) => f.apply);
                const filterSets: ITableFilterSetState[] = tableState?.filterSets ?? [];

                // A set is active when ALL of its filters are present in the applied filters
                // (subset match). Multiple sets can match at once; extra manual filters that
                // belong to no set are ignored. We only flip the active flags (via
                // reconcileActiveSets), never re-derive — so those manual filters stay.
                const matched = filterSets.filter(set =>
                    set.filters.length > 0 &&
                    set.filters.every(sf => appliedFilters.some(cf => sameSetFilter(cf, sf)))
                );

                // At most one active set per single-select group; multi-select groups keep them all.
                const matchedIds = dropExclusiveConflicts(matched.map(s => s.id), filterSets);
                const currentIds = filterSets.filter(s => s.active).map(s => s.id);
                const unchanged = matchedIds.length === currentIds.length &&
                    [...matchedIds].sort().join(',') === [...currentIds].sort().join(',');

                return unchanged ? null : { tableId: action.tableId as PageIdentityEnum, setIds: matchedIds };
            }),
            filter(result => result !== null),
            map(result => FilterSetActions.reconcileActiveSets({ tableId: result!.tableId, setIds: result!.setIds }))
        )
    );

    // When a filter value is manually edited, deactivate only the active set(s) that own that
    // field once the value no longer matches the set's definition. Editing a field that no
    // active set uses leaves the sets alone. The value-match check makes this immune to the
    // set-selection feedback loop: re-applying a set re-emits updateFilter with the set's own
    // value, which still matches, so the set is not deactivated.
    deactivateSetsOnManualFilterEdit$ = createEffect(() =>
        this.actions$.pipe(
            ofType(TableFilterActions.updateFilter),
            switchMap(action =>
                this.store.select(TableFilterSetSelectors.getFilterSets(action.tableId as PageIdentityEnum)).pipe(
                    take(1),
                    map(sets => (sets ?? [])
                        .filter(s =>
                            s.active &&
                            s.filters.some(f => f.fieldName === action.filter.fieldName) &&
                            !s.filters.some(f => sameSetFilter(f, action.filter))
                        )
                        .map(s => s.id)
                    ),
                    filter(setIds => setIds.length > 0),
                    map(setIds => FilterSetActions.deactivateSets({ tableId: action.tableId as PageIdentityEnum, setIds }))
                )
            )
        )
    );
}
